<!-- BaseTableContent系程リッチじゃなくていいとき用の軽量版(主に確認用モーダルとかに) -->
<script lang="ts" setup>
import { i18n } from '@/i18n';
import { businessContentName } from '@/appLogic/business/businessTableHeaders';
import TableCell from '@/components/atom/TableCell.vue';
import TableHead from '@/components/atom/TableHead.vue';
import { getDisplayArrivalOrSalesResultLabel, getArrivalOrSalesResultLabel } from '@/appLogic/business/businessLogic'; // TODO: 移動対象
import { initialForecast } from '@/appLogic/importData/initialData';
import IconButton from '@/components/input/Btn/IconButton.vue';
import { nullOrUndefinedToMinusOne } from '@/coreDomainsModule/extends/number';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { TableType } from '@/coreDomainsModule/table';
import { TableCommonViewModel, TableContentViewModel } from '@/viewModels/tableContent';

// props
const props = withDefaults(
  defineProps<{
    tableType: TableType;
    items: IBusiness[];
    scrollEnable?: boolean;
    editableLeadTime?: boolean;
    editableStdInventory?: boolean;
  }>(),
  {
    scrollEnable: false,
    editableLeadTime: false,
    editableStdInventory: false,
  }
);

// emits
const emits = defineEmits<{
  (e: 'onClickLeadTimeButton', event: MouseEvent): void;
  (e: 'onClickStdInventoryButton', event: MouseEvent): void;
}>();

//変数
const t = i18n.t;
const commonTableContentVm = new TableCommonViewModel();
const vm = new TableContentViewModel();

// 関数
const onClickLeadTimeButton = (event: MouseEvent) => {
  emits('onClickLeadTimeButton', event);
};
const onClickStdInventoryButton = (event: MouseEvent) => {
  emits('onClickStdInventoryButton', event);
};
</script>

<template>
  <div class="simple-business-table">
    <table class="table-container">
      <thead>
        <tr>
          <template v-for="(property, index) in commonTableContentVm.displayHeaders[tableType]" :key="index">
            <template
              v-if="
                commonTableContentVm.mvpSimpleTableDisplayContents(property) ||
                commonTableContentVm.commonTableDisplayContents(property) ||
                property === 'stdInventoryIndex'
              "
            >
              <TableHead :label="businessContentName[property]" :text-align="'left'" scope="col" />
            </template>

            <template v-if="property === 'arrivals' || property === 'salesResults'">
              <TableHead :label="getArrivalOrSalesResultLabel(property)" />
            </template>

            <template v-if="property === 'announce-1'">
              <TableHead
                v-for="(announce, indexAnnounce) in initialForecast.customerAnnounce"
                :key="index + indexAnnounce"
                :label="vm.getForecastLabel(indexAnnounce)"
              />
            </template>
          </template>
        </tr>
      </thead>
      <tbody class="body-container">
        <template v-for="(item, itemIndex) in items" :key="itemIndex">
          <tr class="item-container">
            <template v-for="(property, propertyIndex) in commonTableContentVm.displayHeaders[tableType]" :key="propertyIndex">
              <template
                v-if="
                  commonTableContentVm.mvpSimpleTableDisplayContents(property) || commonTableContentVm.commonTableDisplayContents(property)
                "
              >
                <template v-if="property === 'inventory' || property === 'package'">
                  <TableCell :row-value="vm.getDisplayValue(item[property], property)" :text-align="'center'">
                    {{ vm.getDisplayValue(item[property], property) }}
                  </TableCell>
                </template>
                <template v-else-if="property === 'leadTime'">
                  <TableCell class="editable-cell" :row-value="vm.getDisplayValue(item[property], property)" :text-align="'center'">
                    <div class="editable-cell__edit-content edit-content">
                      {{ vm.getLeadTime(nullOrUndefinedToMinusOne(item[property])) }}
                      <!-- 2023-12-25 デモのために一旦非表示 -->
                      <!-- <IconButton
                        v-if="editableLeadTime"
                        class="edit-content__button"
                        :icon-name="'pen'"
                        :tool-tip-value="'リードタイムを変更'"
                        :button-style="'btn-primary'"
                        :on-click="onClickLeadTimeButton"
                      /> -->
                    </div>
                  </TableCell>
                </template>
                <template v-else>
                  <TableCell :tool-tip-value="vm.getDisplayValue(item[property], property)" :text-align="'left'">
                    {{ vm.getDisplayValue(item[property], property) }}
                  </TableCell>
                </template>
              </template>
              <template v-if="property === 'stdInventoryIndex'">
                <TableCell class="item-container__std-inventory-index editable-cell">
                  <div class="editable-cell__edit-content edit-content">
                    {{ vm.getDisplayStdInventory(item) }}
                    <!-- 2023-12-25 デモのために一旦非表示 -->
                    <!-- <IconButton
                      v-if="editableStdInventory"
                      class="edit-content__button"
                      :icon-name="'pen'"
                      :tool-tip-value="'標準在庫指数を変更'"
                      :button-style="'btn-primary'"
                      :on-click="onClickStdInventoryButton"
                    /> -->
                  </div>
                </TableCell>
              </template>

              <template v-if="property === 'arrivals' || property === 'salesResults'">
                <TableCell :text-align="'right'">
                  {{ getDisplayArrivalOrSalesResultLabel(item.arrivals ?? []) }}
                </TableCell>
              </template>

              <template v-if="property === 'announce-1'">
                <template
                  v-for="(announce, announce_index) in vm.fetchForecastSpecifiedMonthsMinutes(item.customerAnnounce)"
                  :key="contentIndex + announce_index"
                >
                  <TableCell :text-align="'right'">
                    {{ vm.getDisplayForecast(announce) }}
                  </TableCell>
                </template>
              </template>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.simple-business-table {
  overflow: v-bind('props.scrollEnable ? `scroll` : `hidden`');
  border-radius: 8px;
  border-width: 1px;
  border-color: $border;
  box-shadow: $boxShadowM;
}
.table-container {
  width: 100%;
  border-collapse: collapse;
  background-color: $semanticWhite;
  font-size: 14px; // TODO: 一旦tailwindのtext-smに合わせてるが、他のテーブルとも帳尻合わせつつちょうどいい値に直す
  line-height: 20px;
}
.body-container {
  border-top-width: 1px;
  border-bottom-width: 0px;
  border-color: $border;
}
.item-container {
  &__std-inventory-index {
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.edit-content {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  &__button {
    width: fit-content;
    margin: auto;
  }
}
</style>
